// @TODO [CX-663]: clean up this file when workspaces are removed

import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { tokens, Icon, Loader, Text, Button } from '@hotjar/hotsauce';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useCurrentSite } from '@hotjar/site';
import { useSamplingState } from '@hotjar/webapp/hooks/useSamplingState';
import { HEATMAPS } from '@hotjar/webapp/constants/routes';
import { usePinnedPages } from '@hotjar/webapp/domains/heatmaps/hooks/usePinnedPages';
import { Card } from '@hotjar/webapp/components/Card';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import type { SidebarItem } from '@hotjar/webapp/features/Heatmaps/SideBar/SideBar';
import { getHeatmapRoute } from '@hotjar/webapp/features/Heatmaps/utils/getHeatmapRoute';
import { NewEmptyWidget, EMPTY_STATE_KIND, EMPTY_STATE_TOOL } from '../NewEmptyWidget';
import { WidgetGhostButton } from '../WidgetGhostButton';
import { getForYouExperimentWidgetHeaderConfig } from '../../utils';

const sortByTitle = (itemA: SidebarItem, itemB: SidebarItem): number =>
  itemA.name.localeCompare(itemB.name);

const MAX_NUMBER_OF_ITEMS = 5;
const TRACKING_UI_POSITION = 'Space Overview Heatmaps Card';

const StyledLoader = styled(Loader)`
  padding: ${tokens.spacer.x4};
`;

const PopularPageItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
  border-bottom: 1px solid ${tokens.color.neutral_100};
  background-color: ${tokens.color.ui_background};

  &:last-of-type {
    border: none;
  }

  &:hover {
    cursor: pointer;
    background-color: ${tokens.color.bg_hover};
    transition: background-color 300ms linear;
  }
`;

const PopularPageUrl = styled.div`
  display: flex;
  flex: 1 1 auto;
  overflow: hidden;
  flex-direction: column;
  margin-right: ${tokens.spacer.x3};

  & > * {
    display: -webkit-box;
    -webkit-line-clamp: 0;
    -webkit-box-orient: vertical;
  }
`;

const ListFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: ${tokens.spacer.x1pt5};
`;

const EmptyState = styled.div`
  height: 240px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  margin: 0 auto;
`;

const EmptyText = styled(Text)`
  margin: ${tokens.spacer.x2} 0 ${tokens.spacer.x0};
`;

export function HeatmapsCard({
  inForYouExperiment,
}: {
  inForYouExperiment?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const siteId = currentSite?.id;
  const heatmapsPath = getRoute(HEATMAPS.LIST_CONTINUOUS_CAPTURE.path, {
    siteId: siteId as number,
  });
  const { click: trackClickEvent } = useTrack();
  const navigate = useNavigate();
  const { getAllPins } = usePinnedPages({
    siteId: siteId as number,
  });
  const { data, isLoading, isError } = getAllPins;
  const trackingCodeState = useTrackingCodeState();
  const { isLoading: isSamplingStateLoading, inDisabledState, inWarningState } = useSamplingState();
  const isEmpty = !data || data.length === 0;
  const trackClick = (elementName: string) =>
    trackClickEvent(elementName, TRACKING_UI_POSITION, {});

  const onViewHeatmapClick = (url: string, match: string) => {
    if (siteId) {
      trackClick('View Heatmap');
      const heatmapsRoute = getHeatmapRoute(siteId.toString(), { url, match });
      navigate(heatmapsRoute);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const renderEmptyState = () => {
    if (inDisabledState || inWarningState) {
      return <NewEmptyWidget tool={EMPTY_STATE_TOOL.heatmaps} kind={EMPTY_STATE_KIND.ERROR} />;
    }

    return null;
  };

  const renderCardBody = () => {
    return isEmpty ? (
      <>
        {!isError && !(inDisabledState || inWarningState) ? (
          <NewEmptyWidget tool={EMPTY_STATE_TOOL.heatmaps} kind={EMPTY_STATE_KIND.EMPTY} />
        ) : (
          <>
            {isError ? (
              <Card.Body>
                <EmptyState>
                  <Icon icon="error" color="text_error" size="l" />
                  <EmptyText
                    style={{ marginBottom: tokens.spacer.x2 }}
                    as="span"
                    textType="body2"
                    textColor="text_light"
                  >
                    {t('activation:dashboard.heatmaps-card.spaces-no-pages-available')}
                  </EmptyText>
                  <Button kind="secondary" onClick={handleRefreshPage}>
                    {t('activation:dashboard.heatmaps-card.refresh-your-browser')}
                  </Button>
                </EmptyState>
              </Card.Body>
            ) : (
              renderEmptyState()
            )}
          </>
        )}
      </>
    ) : (
      <div data-testid="card-content">
        {data
          ?.slice(0, MAX_NUMBER_OF_ITEMS)
          .sort(sortByTitle)
          .map(({ id, name, pattern, matchOperation }) => (
            <PopularPageItem
              role="button"
              onClick={() => onViewHeatmapClick(pattern, matchOperation)}
              key={id}
            >
              <PopularPageUrl>
                <Text
                  css={{ marginBottom: tokens.spacer.x1 }}
                  textType="body2"
                  textColor="text_default"
                >
                  {name}
                </Text>
                <Text textType="caption" textColor="text_light">
                  {pattern}
                </Text>
              </PopularPageUrl>
              <WidgetGhostButton
                size="s"
                onClick={() => onViewHeatmapClick(pattern, matchOperation)}
              >
                {t('activation:dashboard.heatmaps-card.view-heatmap')}
              </WidgetGhostButton>
            </PopularPageItem>
          ))}
        <ListFooter>
          <WidgetGhostButton
            as="a"
            href={heatmapsPath}
            onClick={() => trackClick('Card Footer Button')}
          >
            {t('activation:dashboard.heatmaps-card.footer')}
          </WidgetGhostButton>
        </ListFooter>
      </div>
    );
  };

  return (
    <Card>
      {isLoading || isSamplingStateLoading || !siteId ? (
        <StyledLoader />
      ) : (
        <>
          <Card.Header
            icon={getForYouExperimentWidgetHeaderConfig('heatmaps', inForYouExperiment).icon}
            variant={getForYouExperimentWidgetHeaderConfig('heatmaps', inForYouExperiment).variant}
            href={heatmapsPath}
            onClick={() => trackClick('Card Title')}
            title={t('activation:dashboard.heatmaps-card.title')}
          />
          {!trackingCodeState.installed ? (
            <NewEmptyWidget tool={EMPTY_STATE_TOOL.heatmaps} kind={EMPTY_STATE_KIND.NO_TC} />
          ) : (
            renderCardBody()
          )}
        </>
      )}
    </Card>
  );
}
